/**
 * SuperBrain Authentication Page
 * Login + 2-step Register (email/password → phone verification)
 * © 2026 A-1 Affiliate Marketing LLC
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './AuthPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Persistent device ID for trial-abuse prevention
const getDeviceId = () => {
  let id = localStorage.getItem('sb_device_id');
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : 'dev-' + Math.random().toString(36).slice(2) + Date.now());
    localStorage.setItem('sb_device_id', id);
  }
  return id;
};

// Read referral code from ?ref= URL param, store, and carry through signup
const getReferralCode = () => {
  try {
    const url = new URL(window.location.href);
    const fromQuery = url.searchParams.get('ref');
    if (fromQuery) {
      localStorage.setItem('sb_ref_code', fromQuery.toUpperCase());
    }
  } catch (e) { /* ignore */ }
  return localStorage.getItem('sb_ref_code') || '';
};

const AuthPage = ({ onAuthenticated, initialPlan = null, initialMode = 'login' }) => {
  const onAuthenticatedRef = useRef(onAuthenticated);
  useEffect(() => {
    onAuthenticatedRef.current = onAuthenticated;
  }, [onAuthenticated]);

  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState(1); // 1 = email/password, 2 = phone, 3 = code
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [plan, setPlan] = useState(initialPlan || 'professional');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);

  useEffect(() => {
    getDeviceId();
    // If returning from Stripe Checkout setup, confirm the card was attached
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment_setup') === 'success' && params.get('session_id')) {
      const token = localStorage.getItem('token');
      if (token) {
        axios.post(`${API}/billing/confirm-card-setup`,
          { session_id: params.get('session_id') },
          { headers: { Authorization: `Bearer ${token}` }})
          .then(() => {
            const u = JSON.parse(localStorage.getItem('user') || '{}');
            u.payment_method_attached = true;
            localStorage.setItem('user', JSON.stringify(u));
            window.history.replaceState({}, '', '/');
            if (onAuthenticatedRef.current) onAuthenticatedRef.current(u);
          })
          .catch(() => { /* user can retry from Settings */ });
      }
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password });
      if (r.data.token) {
        localStorage.setItem('token', r.data.token);
        localStorage.setItem('user', JSON.stringify(r.data.user));
        onAuthenticated(r.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleStep1Next = (e) => {
    e.preventDefault();
    setError('');
    if (!agreedToTerms || !agreedToPrivacy) {
      setError('You must agree to the Terms of Service and Privacy Policy to register');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setStep(2);
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError(''); setInfo(''); setLoading(true);
    try {
      // Pre-check eligibility (phone/device/IP)
      const eligibility = await axios.post(`${API}/phone/check-trial-eligibility`, {
        phone_number: phoneNumber,
        device_id: getDeviceId()
      });
      if (!eligibility.data.eligible) {
        setError(eligibility.data.reason || 'Not eligible for trial');
        setLoading(false);
        return;
      }
      // Send code
      await axios.post(`${API}/phone/send-code`, { phone_number: phoneNumber });
      setCodeSent(true);
      setStep(3);
      setInfo('Verification code sent. Check your phone for the 6-digit code.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send verification code. Make sure your phone number is valid (include country code, e.g. +15551234567).');
    } finally { setLoading(false); }
  };

  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      // Verify code
      const v = await axios.post(`${API}/phone/verify-code`, {
        phone_number: phoneNumber,
        code: verificationCode
      });
      if (!v.data.verified) {
        setError('Invalid verification code');
        setLoading(false);
        return;
      }
      // Register account
      const r = await axios.post(`${API}/auth/register`, {
        email, password, plan,
        phone_number: phoneNumber,
        phone_verified: true,
        device_id: getDeviceId(),
        referral_code: getReferralCode()
      });
      if (r.data.token) {
        localStorage.setItem('token', r.data.token);
        localStorage.setItem('user', JSON.stringify(r.data.user));
        // Move to Step 4: collect payment method (only for trial-eligible plan)
        if (plan === 'commander') {
          setStep(4);
          setInfo('Account created. Add a payment method to start your free trial. We will not charge you until your trial ends — cancel anytime.');
        } else {
          onAuthenticated(r.data.user);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification or registration failed');
    } finally { setLoading(false); }
  };

  const handleAddPaymentMethod = async () => {
    setError(''); setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/billing/setup-card-session`, {
        success_url: `${window.location.origin}/?payment_setup=success`,
        cancel_url: `${window.location.origin}/?payment_setup=cancel`
      }, { headers: { Authorization: `Bearer ${token}` }});
      // Redirect to Stripe-hosted card collection page
      window.location.href = r.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start card setup. You can add it later from Settings.');
    } finally { setLoading(false); }
  };

  const skipPaymentMethod = () => {
    // Allow skip but warn the user
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    onAuthenticated(user);
  };

  const resendCode = async () => {
    setError(''); setInfo(''); setLoading(true);
    try {
      await axios.post(`${API}/phone/send-code`, { phone_number: phoneNumber });
      setInfo('New verification code sent.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
    } finally { setLoading(false); }
  };

  return (
    <div className="auth-page" data-testid="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1>SuperBrain</h1>
          <p className="auth-subtitle">AGENTIC AGGREGATE · Multi-Model Verification</p>
        </div>

        <div className="auth-tabs">
          <button
            className={`auth-tab ${mode === 'login' ? 'active' : ''}`}
            onClick={() => { setMode('login'); setStep(1); setError(''); }}
            data-testid="login-tab"
          >Login</button>
          <button
            className={`auth-tab ${mode === 'register' ? 'active' : ''}`}
            onClick={() => { setMode('register'); setStep(1); setError(''); }}
            data-testid="register-tab"
          >Register</button>
        </div>

        {mode === 'login' && (
          <form className="auth-form" onSubmit={handleLogin}>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     required placeholder="your@email.com" data-testid="login-email" />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     required placeholder="Enter password" data-testid="login-password" />
            </div>
            {error && <div className="auth-error" data-testid="auth-error">{error}</div>}
            <button type="submit" className="auth-submit" disabled={loading} data-testid="login-submit-btn">
              {loading ? 'Processing...' : 'LOGIN'}
            </button>
            <a
              href="/forgot-password"
              className="auth-link-btn"
              style={{ display: 'block', textAlign: 'center', marginTop: '0.75rem' }}
              data-testid="forgot-password-link"
            >
              Forgot password?
            </a>
          </form>
        )}

        {mode === 'register' && step === 1 && (
          <form className="auth-form" onSubmit={handleStep1Next}>
            <div className="step-indicator">Step 1 of 2 · Account Details</div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     required placeholder="your@email.com" data-testid="register-email" />
            </div>
            <div className="form-group">
              <label>Password (min 8 chars)</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     required placeholder="Create password" data-testid="register-password" />
            </div>
            <div className="form-group">
              <label>Select Plan</label>
              <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="register-plan">
                <option value="professional">Professional — $59/mo (225 queries) — 3-DAY FREE TRIAL</option>
                <option value="business">Business — $99/mo (400 queries) — 3-DAY FREE TRIAL</option>
                <option value="enterprise">Enterprise — $249/mo (1,000 queries) — 3-DAY FREE TRIAL</option>
                <option value="ultimate">Ultimate — $499/mo (2,000 queries) — 3-DAY FREE TRIAL</option>
              </select>
              <p className="trial-note">✝ 3-Day Free Trial · Card on file · No charge before Day 4 ✝</p>
            </div>

            <div className="legal-agreements">
              <div className="checkbox-group">
                <input type="checkbox" id="terms-check" checked={agreedToTerms}
                       onChange={(e) => setAgreedToTerms(e.target.checked)} required data-testid="terms-check" />
                <label htmlFor="terms-check">
                  I have read and agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>,{' '}
                  <a href="/disclaimer" target="_blank" rel="noopener noreferrer">Disclaimer</a>,{' '}
                  <a href="/refund" target="_blank" rel="noopener noreferrer">Refund Policy</a>, and{' '}
                  <a href="/dmca" target="_blank" rel="noopener noreferrer">DMCA Policy</a>, including the{' '}
                  <strong>limitation of liability, indemnification, and binding arbitration / class-action waiver</strong> provisions. *
                </label>
              </div>
              <div className="checkbox-group">
                <input type="checkbox" id="privacy-check" checked={agreedToPrivacy}
                       onChange={(e) => setAgreedToPrivacy(e.target.checked)} required data-testid="privacy-check" />
                <label htmlFor="privacy-check">
                  I have read and agree to the{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and consent to my data being processed as described. *
                </label>
              </div>
              <div className="liability-notice">
                ⚠️ <strong>Important:</strong> SuperBrain output may contain errors and is provided "AS IS." It is NOT legal, medical, financial, mental-health, or safety advice. You alone are responsible for verifying information and for any decisions you take. Disputes are resolved in <strong>Kansas</strong> by individual binding arbitration; class actions are waived.
              </div>
            </div>

            {error && <div className="auth-error" data-testid="auth-error">{error}</div>}
            <button type="submit" className="auth-submit" disabled={loading} data-testid="register-next-btn">
              NEXT → Verify Phone
            </button>
          </form>
        )}

        {mode === 'register' && step === 2 && (
          <form className="auth-form" onSubmit={handleSendCode}>
            <div className="step-indicator">Step 2 of 2 · Phone Verification</div>
            <p className="auth-help">
              We verify every account via SMS to prevent trial abuse and protect your subscription.
              Standard SMS rates apply.
            </p>
            <div className="form-group">
              <label>Phone Number (with country code)</label>
              <input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)}
                     required placeholder="+1 555 123 4567" pattern="^\+?[0-9\s\-]{8,20}$"
                     data-testid="register-phone" />
              <small className="hint">Example: +15551234567</small>
            </div>
            {info && <div className="auth-info" data-testid="auth-info">{info}</div>}
            {error && <div className="auth-error" data-testid="auth-error">{error}</div>}
            <div className="two-button-row">
              <button type="button" className="auth-secondary" onClick={() => { setStep(1); setError(''); }}
                      data-testid="back-to-step1-btn">← Back</button>
              <button type="submit" className="auth-submit" disabled={loading || !phoneNumber}
                      data-testid="send-code-btn">
                {loading ? 'Sending...' : 'Send SMS Code'}
              </button>
            </div>
          </form>
        )}

        {mode === 'register' && step === 3 && (
          <form className="auth-form" onSubmit={handleVerifyAndRegister}>
            <div className="step-indicator">Enter 6-Digit Code</div>
            <p className="auth-help">
              We sent a code to <strong>{phoneNumber}</strong>. Enter it below to complete registration.
            </p>
            <div className="form-group">
              <label>Verification Code</label>
              <input type="text" inputMode="numeric" maxLength={6} value={verificationCode}
                     onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ''))}
                     required placeholder="123456" className="code-input"
                     data-testid="verification-code" />
            </div>
            {info && <div className="auth-info" data-testid="auth-info">{info}</div>}
            {error && <div className="auth-error" data-testid="auth-error">{error}</div>}
            <div className="two-button-row">
              <button type="button" className="auth-secondary" onClick={resendCode} disabled={loading}
                      data-testid="resend-code-btn">Resend Code</button>
              <button type="submit" className="auth-submit" disabled={loading || verificationCode.length !== 6}
                      data-testid="verify-register-btn">
                {loading ? 'Verifying...' : 'Verify & Create Account'}
              </button>
            </div>
            <button type="button" className="auth-link-btn" onClick={() => { setStep(2); setError(''); setInfo(''); }}
                    data-testid="change-phone-btn">← Use a different phone number</button>
          </form>
        )}

        {mode === 'register' && step === 4 && (
          <div className="auth-form" data-testid="payment-step">
            <div className="step-indicator">Last Step · Add Payment Method</div>
            <div className="trial-explainer">
              <h3 style={{ color: '#C8860A', textAlign: 'center', margin: '0 0 1rem' }}>
                🎁 Your 3-Day Free Trial Awaits
              </h3>
              <ul className="trial-bullets">
                <li>✅ Full access to all 6 AI models for 3 days</li>
                <li>✅ 3 queries on Day 1, 2 on Day 2, 1 on Day 3 — full 7-AI power every day</li>
                <li>✅ Cancel anytime from your dashboard — no questions asked</li>
                <li>💳 Card secured but NOT charged until trial ends</li>
                <li>🔔 Email reminder 24h before trial ends</li>
              </ul>
              <p className="trial-fineprint">
                We require a payment method to prevent fraud and ensure trial quality.
                Your card stays safely with Stripe (PCI-DSS Level 1 certified).
                If you cancel before Day 4, you pay $0.
              </p>
            </div>
            {info && <div className="auth-info" data-testid="auth-info">{info}</div>}
            {error && <div className="auth-error" data-testid="auth-error">{error}</div>}
            <button
              type="button"
              className="auth-submit"
              onClick={handleAddPaymentMethod}
              disabled={loading}
              data-testid="add-payment-btn"
            >
              {loading ? 'Redirecting to Stripe...' : '🔒 Secure Payment Method →'}
            </button>
            <button
              type="button"
              className="auth-link-btn"
              onClick={skipPaymentMethod}
              disabled={loading}
              data-testid="skip-payment-btn"
            >
              Skip for now (limited access — no trial benefits)
            </button>
          </div>
        )}

        <div className="auth-footer">
          <p>✝ GOD BLESS EVERYONE ✝</p>
          <p className="auth-copyright">© 2026 A-1 Affiliate Marketing LLC</p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
